import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import { StyledHome } from "./home-style";
import whyvector from '../../images/home/whyvector.svg';

import step1 from '../../images/home/step1.svg';
import step2 from '../../images/home/step2.svg';
import step3 from '../../images/home/step3.svg';
import step4 from '../../images/home/step4.svg';

import allthingsaiImg from '../../images/feat/allthingsai.png';
import designertipsImg from '../../images/feat/designertips.png';
import futuretoolsImg from '../../images/feat/futuretools.png';
import mediumImg from '../../images/feat/medium.png';
import producthuntImg from '../../images/feat/producthunt.png';
import loveaitoolsImg from '../../images/feat/loveaitools.png';
import startIcon from '../../images/icons/start.svg';

import { logEvent } from "firebase/analytics";
import { analytics } from "../../fire";
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleXmark, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { COLORS } from "../../colors";
import { Button } from "../../components/button/Button";
import LazyImage from "../../components/LazyImage";
import { useTranslation } from 'react-i18next';
import { getLocalizedPath } from "../../utils/lang";
import { AlternateLinks } from "../../components/AlternateLinks";
import { PromptTipsModal } from "../../components/prompt-tips-modal/PromptTipsModal";

export const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPromptTipsModal, setShowPromptTipsModal] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>("");
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleClickSubmit = () => {
    if (prompt.length > 0) {
      logEvent(analytics, 'select_prompt', { prompt });
      navigate(getLocalizedPath('/generate'), {
        state: {
          prompt
        }
      });
    } else {
      setIsHighlighted(true);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPrompt(value);
    
    if (isHighlighted && value.length > 0) {
      setIsHighlighted(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default to avoid newline in textarea
      handleClickSubmit();
    }
  };
  
  return (
    <>
      <Helmet>
        <title>{t('home_seo_title')}</title>
        <meta property="og:title" content={t('home_seo_title')} />
        <meta name="twitter:title" content={t('home_seo_title')} />
        <meta name="description" content={t('home_seo_description')} />
        <meta name="twitter:description" content={t('home_seo_description')} />
        <meta property="og:url" content={window.location.href} />
        {AlternateLinks().map((link, index) => (
          <link key={index} {...link} />
        ))}
      </Helmet>
      <StyledHome>
        <section id="main">
          <div>
            <div className="titles">
              <div className="counter">
                {t('home_counter')}
              </div>
              <h1>{t('home_h1_1')} <span id="vector">{t('home_h1_vector_illustrations')}</span> {t('home_h1_2')}</h1>
              <p>{t('home_what_illustrate')}</p>
            </div>
            <div className="inputs">
              <textarea 
                ref={textareaRef}
                value={prompt} 
                onChange={handlePromptChange}
                onKeyDown={handleKeyDown}
                placeholder={t('home_prompt_placeholder')} 
                className={isHighlighted ? "highlighted" : ""}
              />
              <button onClick={handleClickSubmit}>
                <img src={startIcon} alt="start" width={25} height={25} />
              </button>
            </div>
            <div className="tips">
              <button onClick={() => setShowPromptTipsModal(true)}>
                <FontAwesomeIcon icon={faQuestionCircle} color={COLORS.primary} />
                {t('home_prompt_tips')}
              </button>
            </div>
          </div>
          <div className="absolute-illustration">
            <LazyImage src={`${window.location.origin}/bee.svg`} alt="illustroke bee" />
          </div>
        </section>
        <section id="example">
          <div className="titles">
            <h1>{t('home_vectorize_imagination')}</h1>
            <p>{t('home_illustroke_intro')}</p>
            <p>{t('home_illustroke_website_logos_icons')}</p>
          </div>
          <div className="examples">
            <div>
              <LazyImage src={`${window.location.origin}/example1.svg`} alt="illustroke example 1" />
              <p>{t('home_example1')}</p>
            </div>
            <div>
              <LazyImage src={`${window.location.origin}/example2.svg`} alt="illustroke example 2" />
              <p>{t('home_example2')}</p>
            </div>
            <div>
              <LazyImage src={`${window.location.origin}/example3.svg`} alt="illustroke example 3" />
              <p>{t('home_example3')}</p>
            </div>
            <div>
              <LazyImage src={`${window.location.origin}/example4.svg`} alt="illustroke example 4" />
              <p>{t('home_example4')}</p>
            </div>
            <div>
              <LazyImage src={`${window.location.origin}/example5.svg`} alt="illustroke example 5" />
              <p>{t('home_example5')}</p>
            </div>
            <div>
              <LazyImage src={`${window.location.origin}/example6.svg`} alt="illustroke example 6" />
              <p>{t('home_example6')}</p>
            </div>
          </div>
        </section>
        <section id="whyvector">
          <img className="whyVectorImg" src={whyvector} alt="why vector" />
          <div className="explanation">
            <h1>{t('home_why_vector')}</h1>
            <p>{t('home_why_vector_desc')}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>{t('home_vector_svg')}</th>
                <th>{t('home_pixels_png')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('home_scale_image')}</td>
                <td><FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary}  /></td>
                <td><FontAwesomeIcon icon={faCircleXmark} color={COLORS.mainGray}  /></td>
              </tr>
              <tr>
                <td>{t('home_change_colors')}</td>
                <td><FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary}  /></td>
                <td><FontAwesomeIcon icon={faCircleXmark} color={COLORS.mainGray}  /></td>
              </tr>
              <tr>
                <td>{t('home_print_high_quality')}</td>
                <td><FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary}  /></td>
                <td><FontAwesomeIcon icon={faCircleXmark} color={COLORS.mainGray}  /></td>
              </tr>
              <tr>
                <td>{t('home_apply_animation')}</td>
                <td><FontAwesomeIcon icon={faCheckCircle} color={COLORS.primary}  /></td>
                <td><FontAwesomeIcon icon={faCircleXmark} color={COLORS.mainGray}  /></td>
              </tr>
              <tr>
                <td>{t('home_average_file_size')}</td>
                <td><b>50kB</b></td>
                <td><b>500kB</b></td>
              </tr>
            </tbody>
          </table>
        </section>
        <section id="howitworks">
          <div className="titles">
            <h1>{t('home_simple')}</h1>
            <p>{t('home_simple_steps')}</p>
          </div>
          <div className="howitworks-container">
            <div>
              <div className="number"><span>1</span></div>
              <img src={step1} alt="why vector" />
              <h2>{t('home_step1_title')}</h2>
              <p>{t('home_step1_desc')}</p>
            </div>
            <div>
              <div className="number"><span>2</span></div>
              <img src={step2} alt="why vector" />
              <h2>{t('home_step2_title')}</h2>
              <p>{t('home_step2_desc')}</p>
            </div>
            <div>
              <div className="number"><span>3</span></div>
              <img src={step3} alt="why vector" />
              <h2>{t('home_step3_title')}</h2>
              <p>{t('home_step3_desc')}</p>
            </div>
            <div>
              <div className="number"><span>4</span></div>
              <img src={step4} alt="why vector" />
              <h2>{t('home_step4_title')}</h2>
              <p>{t('home_step4_desc')}</p>
            </div>
          </div>
        </section>
        <section id="alltools">
          <div className="titles">
            <h1>{t('home_discover_tools')}</h1>
            <p>{t('home_discover_tools_desc')}</p>
          </div>
          <div className="cta">
            <Button variant="link" to={getLocalizedPath("/image-to-vector")}>{t('home_image_to_vector')}</Button>
            <Button variant="link" to={getLocalizedPath("/image-generator")}>{t('home_pixel_illustrations')}</Button>
          </div>
        </section>
        <section id="seenin">
          <div className="titles">
            <h1>{t('home_loved_by')}</h1>
          </div>
          <div className="images">
            <a target="_blank" rel="noopener noreferrer" href="https://medium.com/illumination/the-must-have-tool-for-writers-to-create-killer-vector-images-e62509bfef97"><img src={mediumImg} alt="medium logo" /></a>
            <a target="_blank" rel="noopener noreferrer" href="https://www.designer.tips/tools/illustroke"><img src={designertipsImg} alt="designer.tips logo" /></a>
            <a target="_blank" rel="noopener noreferrer" href="https://allthingsai.com/tool/illustroke"><img src={allthingsaiImg} alt="allthigs ai logo" /></a>
          </div>
          <div className="images">
            <a target="_blank" rel="noopener noreferrer" href="https://www.futuretools.io/tools/illustroke"><img src={futuretoolsImg} alt="futuretools logo" /></a>
            <a target="_blank" rel="noopener noreferrer" href="https://www.producthunt.com/products/illustroke"><img src={producthuntImg} alt="producthunt logo" /></a>
            <a target="_blank" rel="noopener noreferrer" href="https://loveaitools.com/aitool/illustroke/"><img src={loveaitoolsImg} alt="loveaitools logo" /></a>
          </div>
        </section>
        <PromptTipsModal show={showPromptTipsModal} handleClose={() => setShowPromptTipsModal(false)} />
      </StyledHome>
    </>
  )
};